import {
  signupSchema,
  type VerificationRequired,
} from "@reelio/contracts";
import { Check } from "lucide-react";
import { useState, type FormEvent } from "react";
import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  ApiRequestError,
  resendVerification,
  signup,
} from "./api";
import { useAuth } from "./AuthProvider";
import { AuthShell } from "./AuthShell";
import { Field, PrimaryButton } from "./FormControls";
import { getSafeRedirect } from "./redirect";

type SignupDraft = {
  email: string;
  businessName: string;
  instagramHandle: string;
  password: string;
};

type FieldErrors = Partial<Record<keyof SignupDraft, string>>;

const firstError = (
  errors: Record<string, string[] | undefined>,
  field: keyof SignupDraft,
) => errors[field]?.[0];

export const SignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthenticated } = useAuth();
  const [draft, setDraft] = useState<SignupDraft>({
    email: "",
    businessName: "",
    instagramHandle: "",
    password: "",
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pending, setPending] = useState<VerificationRequired | null>(null);
  const [resendMessage, setResendMessage] = useState("");
  const loginPath = `/auth/login${location.search}`;

  const updateDraft = (field: keyof SignupDraft, value: string) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setFormError("");
  };

  const completeSignup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = signupSchema.safeParse(draft);

    if (!parsed.success) {
      const fieldErrors = parsed.error.flatten().fieldErrors;
      setErrors({
        email: firstError(fieldErrors, "email"),
        businessName: firstError(fieldErrors, "businessName"),
        instagramHandle: firstError(fieldErrors, "instagramHandle"),
        password: firstError(fieldErrors, "password"),
      });
      return;
    }

    setIsSubmitting(true);
    setFormError("");

    try {
      const result = await signup(parsed.data);
      if ("requiresEmailVerification" in result) {
        setPending(result);
        return;
      }
      setAuthenticated(result);
      navigate(getSafeRedirect(location.search), { replace: true });
    } catch (error) {
      if (error instanceof ApiRequestError) {
        setFormError(error.message);
        if (error.fieldErrors) {
          setErrors({
            email: error.fieldErrors.email?.[0],
            businessName: error.fieldErrors.businessName?.[0],
            instagramHandle: error.fieldErrors.instagramHandle?.[0],
            password: error.fieldErrors.password?.[0],
          });
        }
      } else {
        setFormError("Reelio could not create the account. Try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resend = async () => {
    if (!pending) return;
    setResendMessage("");
    try {
      const result = await resendVerification({ email: pending.email });
      setResendMessage(result.message);
    } catch (error) {
      setResendMessage(
        error instanceof ApiRequestError
          ? error.message
          : "We could not resend the verification email.",
      );
    }
  };

  return (
    <AuthShell
      title={pending ? "Check your email" : "Create an account"}
      description={
        pending
          ? `We sent a verification link to ${pending.email}.`
          : "Create your workspace to turn Shopify products into a month of Instagram content."
      }
      footer={
        pending ? null : (
          <p>
            Already have an account? <Link to={loginPath}>Log in</Link>
          </p>
        )
      }
    >
      {pending ? (
        <section className="auth-success" aria-live="polite">
          <span className="auth-success__icon" aria-hidden="true">
            <Check size={20} strokeWidth={2} />
          </span>
          <h2>Verify your email to continue.</h2>
          <p>The link expires in 24 hours.</p>
          {resendMessage ? <p>{resendMessage}</p> : null}
          <button className="auth-secondary" type="button" onClick={resend}>
            Resend verification email
          </button>
        </section>
      ) : (
        <form className="auth-form" onSubmit={completeSignup} noValidate>
          <Field
            label="Business name"
            name="businessName"
            autoComplete="organization"
            value={draft.businessName}
            error={errors.businessName}
            onChange={(event) =>
              updateDraft("businessName", event.target.value)
            }
          />

          <Field
            label="Work email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={draft.email}
            error={errors.email}
            onChange={(event) => updateDraft("email", event.target.value)}
          />

          <Field
            label="Instagram handle"
            name="instagramHandle"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={draft.instagramHandle}
            error={errors.instagramHandle}
            onChange={(event) =>
              updateDraft("instagramHandle", event.target.value)
            }
          />

          <Field
            label="Password"
            name="password"
            type="password"
            autoComplete="new-password"
            value={draft.password}
            error={errors.password}
            onChange={(event) => updateDraft("password", event.target.value)}
            hint="10+ characters"
          />

          {formError ? (
            <p className="auth-form__alert" role="alert">
              {formError}
            </p>
          ) : null}

          <PrimaryButton
            type="submit"
            isLoading={isSubmitting}
            loadingLabel="Creating workspace…"
          >
            Create workspace
          </PrimaryButton>
        </form>
      )}
    </AuthShell>
  );
};
